import { Component, ElementRef, ViewChild } from "@angular/core";
import { ImportsModule } from "../imports";

import { ScopeService } from "../services/scope.service";
import { MessagingService } from "../services/messaging.service";
import { Table } from "primeng/table";

interface Column {
  actions?: any[];
  key?: boolean;
  label: string;
  field: string;
  width?: number;
  align?: string;
  options?: any[];
  filterValue?: any;
  maxLength?: number,
  upperCase?: boolean;
  hidden?: boolean;
  filterType?: string;
  matchMode?: string;
  editable?: boolean;
  title?: string;
  getText?: boolean;
}

@Component({
  selector: 'app-app-areas',
  standalone: true,
  imports: [ImportsModule],
  templateUrl: './app-areas.component.html',
  styleUrl: './app-areas.component.css'
})
export class AppAreasComponent {
  @ViewChild('dt') dataTable!: Table;
  @ViewChild('newAreaInput') newAreaInput!: ElementRef<HTMLInputElement>;
  refreshButtonText: { label: string; }[] = [{ label: "Refresh" }, { label: "Clear Filters" }];
  showAddDialog: boolean = false;
  isFiltered: boolean = false;
  items: any[] = [];
  totalRecords: number = 0;
  isBusy: boolean = true;
  newRecord: { area: any; text: any; phase: any, notes: any; } = { area: null, text: null, phase: null, notes: null };
  defaultFilters: any = {};
  filters: any = {};
  sortedColumns: any[] = [{ field: 'area', order: 1 }];
  private refreshing: boolean = false;
  phaseOptions!: any[];
  columns!: Column[];
  
  constructor(private scopingService: ScopeService, private messagingService: MessagingService) {
  }

  ngOnInit() {
    this.refresh();
  }

  showAdd() {
    this.showAddDialog = true;
    setTimeout(() => {
      this.newRecord = { area: null, text: null, phase: null, notes: null };
      this.newAreaInput.nativeElement.focus();
    }, 100);
  }

  refresh() {
    this.items.length = 0;
    this.isBusy = true;
    this.refreshing = true;

    // Clear all filters
    for (let filter in this.filters) this.filters[filter].value = null;

    if (this.dataTable) this.dataTable.resetScrollTop();
    this.loadData();
  }

  getFilter(): any {
    const filters: any = {};

    this.isFiltered = false;
    if (this.filters) {
      for (const field in this.filters) {
        const filter: any = this.filters[field] || null;
        if (!filter) continue;
        if (filter.value == null) continue;
        if (this.refreshing) {
          filter.value = null;
        } else {
          if (typeof filter.value === "string" && filter.value.length == 0) filter.value = null;
          if (typeof filter.value === "number" && isNaN(filter.value)) filter.value = null;
          if (Array.isArray(filter.value) && !filter.value.length) filter.value = null;
          const column = this.columns.find((c: any) => c.field === field);
          if (column?.upperCase) filter.value = filter.value.toUpperCase();
          if (column?.filterType === "numeric") filter.value = parseInt(filter.value);
          this.isFiltered = true;
          filters[field] = Object.assign({}, filter);
        }
      }
    }

    if (this.refreshing) this.refreshing = false;

    // Add sorting
    let orderby: any = null;
    if (this.sortedColumns.length) {
      orderby = [];
      for (const column of this.sortedColumns) {
        const col = this.columns.find((c: any) => c.field === column.field);
        if (col) orderby.push({ field: column.field, desc: column.order < 0 ? true : undefined });
      }
    }

    return { filters: filters, orderby: orderby };
  }

  loadData() {
   
    this.scopingService.getScopePhases({orderby: "phase"}).subscribe({
      next: (data: any) => {
        this.phaseOptions = data.items.map((item: { phase: any; }) => ({ label: item.phase, value: item.phase }));
        this.phaseOptions.push({label: "All", value: null})

        const columns: any[] = [
          { field: "id", label: "id", width: 200, maxLength: 20, key: true, hidden: true },
          { field: "area", label: "Area", width: 100, maxLength: 20, editable: true },
          { field: "text", label: "Text", width: 350, maxLength: 50, matchMode: "contains", editable: true },
          { field: "phase", label: "Phase Sequence", width: 50, options: this.phaseOptions, align: "right", matchMode: "equals", editable: true },
          { field: "notes", label: "Notes", width: 350, maxLength: 200, matchMode: "contains", editable: true },
          { field: "", label: "Actions", actions: [{ tooltip: "Delete", icon: "pi pi-trash", action: this.delete, severity: "danger" }] }
        ];

        this.columns = columns;
        const filter = this.getFilter();
        filter.select = this.columns.filter(i => i.field).map((c: any) => c.field);

        this.scopingService.getAppAreas(filter).subscribe({
          next: (data: any) => {
            this.items = data.items;
            this.totalRecords = this.items.length;
          },
          error: (error: any) => {
            this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
          },
          complete: () => {
            this.isBusy = false;
          }
        });
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
    });
  }

  delete(data: any) {
    this.isBusy = true;
    this.scopingService.deleteAppArea(data).subscribe({
      next: (result: any) => {
        this.messagingService.showSuccess("Record deleted.");
        this.items = this.items.filter((i: any) => i.id !== data.id);
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to delete record.", error);
      },
      complete: () => {
        this.isBusy = false;
      }
    });
  }

  addRecord() {
    if (!this.newRecord.area) return;
    this.isBusy = true;
    if (typeof this.newRecord.phase == "string") {
      debugger;
      this.newRecord.phase = parseInt(this.newRecord.phase) || null;
    }
    const newItem: any = Object.assign({ id: crypto.randomUUID(), lastChangedBy: "user", lastChangedDate: new Date() }, this.newRecord);
    this.scopingService.updateAppArea(newItem).subscribe({
      next: (result: any) => {
        this.messagingService.showSuccess("Record added.");
        this.items = this.items.concat(newItem);
        this.showAdd();
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to add record.", error);
      },
      complete: () => {
        this.isBusy = false;
      }
    });
  }

  updateRecord(data: any) {
    if (data && typeof data.phase == "string") {
      debugger;
      data.phase = parseInt(data.phase) || null;
    }
    // Only send the fields that are key or editable
    const fields = this.columns.filter((c: any) => c.key | c.editable);
    const changedObject: any = {};
    for (const field of fields) {
      changedObject[field.field] = data[field.field];
    }
    changedObject.lastChangedBy = "user";
    changedObject.lastChangedDate = new Date();

    this.scopingService.updateAppArea(changedObject).subscribe({
      next: (result: any) => {
        // get item from items and update it
        const item = this.items.find((i: any) => i.id === data.id);
        if (item) {
          for (const field of fields) {
            item[field.field] = data[field.field];
          }
        }
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to update record.", error);
      }
    });
  }

  exportExcel() {
    this.messagingService.showSuccess("Export starting...");
    const filter = this.getFilter();
    filter.select = this.columns.filter(i => i.field).map((c: any) => c.field);
    this.scopingService.getAppAreas(filter).subscribe({
      next: (data: any) => {
        const items: any[] = data.items || data || [];
        import("xlsx").then(xlsx => {
          const worksheet = xlsx.utils.json_to_sheet(items);
          const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
          const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array"
          });
          this.saveAsExcelFile(excelBuffer, "app-areas");
        });
      }
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver-es").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });
      FileSaver.saveAs(
        data,
        fileName + EXCEL_EXTENSION
      );
    });
  }

  handleSort(event: any) {
    this.sortedColumns = event.multisortmeta || event.multiSortMeta;
  }

  handleFilter(event: any) {
    this.totalRecords = event.filteredValue.length;
  }

  getRefreshText() {
    this.getFilter();
    return !this.isFiltered ? this.refreshButtonText[0].label : this.refreshButtonText[1].label;
  }

  doAction(action: any, data: any) {
    if (typeof action == "function") action.call(this, data);
  }

  getText(item: any, column: any) {
    return column.options.find((o: any) => o.value === item[column.field])?.label;
  }

  dropDownOnFocus(dropDown: any) {
    if (dropDown.focused) dropDown.show();
  }
}
